<template>
  <div
    id="sidebar"
    class="menu-container"
    :class="{
      'menu-container--colapsable': props.collapsible,
      'menu-container--hidden': !props.visible,
      'menu-container--is-collapsed': !isExpanded,
    }"
    :style="{ width: isExpanded ? '16rem' : '3.75rem' }"
    @mouseleave="onMouseLeave"
  >
    <div class="menu logo-close tw:px-2">
      <div class="menu-header">
        <template v-if="isExpanded">
          <RouterLink v-if="logomarca || logo" to="/home" class="menu-logo-link tw:pl-2">
            <img
              loading="lazy"
              :src="logomarca || logo"
              alt="logo"
              class="ml-logo"
            />
          </RouterLink>
          <EmptyLogo v-else-if="getIsAdmin && siteLoaded" />
          <img v-else loading="lazy" class="ml-logo" />

          <button
            v-if="!props.collapsible"
            class="menu-toggle-btn"
            :title="$t('menu.collapse_sidebar')"
            @click.stop="toggleCollapsed"
          >
            <IconGoogle name="chevron_left" size="16px" color="var(--text-color)" />
          </button>
        </template>

        <button
          v-else-if="!props.collapsible"
          class="menu-toggle-btn menu-toggle-btn--alone"
          :title="$t('menu.expand_sidebar')"
          @click.stop="toggleCollapsed"
        >
          <IconGoogle name="chevron_right" size="16px" color="var(--text-color)" />
        </button>
      </div>

      <SidebarMenu :collapsed="isCollapsed && !hovered" @mouseenter="onMouseEnter" />
    </div>

    <div v-if="props.collapsible && props.visible" class="sidebar__overlay" @click="layoutStore.actionHideSidebar" />
  </div>
</template>

<script lang="ts" setup>
import SidebarMenu from "./SidebarMenu.vue";
import EmptyLogo from "./EmptyLogo.vue";
import { RouterLink } from "vue-router";
import { computed, ref, watch } from "vue";
import { useStateStore } from "@/store/state.store";
import { useAuthStore } from "@/store/auth.store";
import { useLayoutStore } from "@/store/layout.store";


const props = defineProps<{
  collapsible?: boolean;
  visible?: boolean;
}>();

const authStore = useAuthStore();
const getIsAdmin = computed(() => authStore.isAdmin());

const stateStore = useStateStore();
const layoutStore = useLayoutStore();
const logomarca = computed(() => stateStore.site?.logomarca);
const logo = computed(() => stateStore.site?.logo);
const siteLoaded = computed(() => stateStore.site && Object.keys(stateStore.site).length > 1);

watch(
  () => props.collapsible,
  (newVal) => {
    if (newVal) layoutStore.actionHideSidebar();
  },
  { immediate: true }
);

const collapsed = ref(localStorage.getItem("sidebarCollapsed") === "true");
const hovered = ref(false);

const isCollapsed = computed(() => collapsed.value && !props.collapsible);
const isExpanded = computed(() => !isCollapsed.value || hovered.value);

function toggleCollapsed() {
  if (isExpanded.value) {
    collapsed.value = true;
    hovered.value = false;
  } else {
    collapsed.value = false;
  }
  localStorage.setItem("sidebarCollapsed", String(collapsed.value));
}

function onMouseEnter() {
  if (isCollapsed.value) hovered.value = true;
}

function onMouseLeave() {
  hovered.value = false;
}
</script>

<style lang="scss" scoped>
#sidebar {
  -webkit-overflow-scrolling: touch;
}

.sidebar__overlay {
  position: fixed;
  height: 100vh;
  left: 15rem;
  width: 100%;

  @media screen and (min-width: 768px) {
    position: absolute;
    width: 100vw;
  }
}

.menu-container {
  transition: width 0.25s ease;
  display: flex;
  width: 15rem;
  flex-direction: column;
  align-items: flex-start;
  background: var(--backgroundcolor);

  &--colapsable {
    position: fixed;
    height: 100vh;
    z-index: 9999;
  }

  &--hidden {
    display: none;
  }
}

.menu {
  display: flex;
  padding: 10px 1.5rem 2.5rem 1.5rem;
  flex-direction: column;
  align-items: flex-start;
  gap: 1rem;
  flex: 1 0 0;
  width: 100%;
  border-right: 1px solid var(--sidebar-border);
  overflow: hidden;
  transition: padding 0.25s ease;

  .menu-container--is-collapsed & {
    padding: 10px 0.625rem 2.5rem 0.625rem;
    align-items: center;
  }
}

.menu-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  min-height: 3rem;

  .menu-container--is-collapsed & {
    justify-content: center;
  }
}

.menu-logo-link {
  display: flex;
  align-items: center;
}

.ml-logo {
  height: 45px;
  object-fit: contain;
  max-width: 8rem;
}

.menu-favicon {
  width: 28px;
  height: 28px;
  object-fit: contain;
}

.menu-toggle-btn {
  all: unset;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 1.75rem;
  height: 1.75rem;
  border-radius: 6px;
  flex-shrink: 0;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: var(--neutral-gray-200);

    [data-theme="dark"] & {
      background-color: var(--neutral-gray-700);
    }
  }

}

.menu-toggle-btn--alone {
  margin: auto;
}
</style>
